import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { generate } from 'short-uuid';
import { useMutation } from '@apollo/client';

import AgentPortalError from 'errors/AgentPortalError';
import { customerShape, paymentsShape } from 'constants/propTypes';
import { codes, MARGIN_REFUND, MONEY_TO_POINTS_CONVERSION_RATE, ADDITIONAL_DETAILS_REFUND_KEY_PREFIX } from 'constants/payment';
import CardSelect from 'components/ui/CardSelect';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { ACTION_CODES } from 'constants/errors';

import LoyaltyMismatchModal from 'components/shared/LoyaltyMismatchModal';
import { getBookingAttachedLoyaltyAccount } from 'utils/booking';
import { ADJUST_POINTS_ENHANCEMENT, ADD_ADDITIONAL_DETAILS, ADD_NOTE_TO_BOOKING } from '../../../../../store/mutations';
import SplitPayment from '../../SplitPayment';
import { Errors } from '../../SplitPayment/SplitPayment.styles';
import { handleRefundSubmit } from '../../SplitPayment/SplitPayment.utils';

import { RefundLabel, HereLink } from './LoyaltyPointsRefund.styles';

const LoyaltyPointsRefund = ({
  loading,
  currency,
  totalPriceToBePaid,
  addPayment,
  addPayoutAndBalance,
  bookingReference,
  method,
  reference,
  updatePayments,
  customer,
  payments,
  additionalDetails = [],
  username,
  isOverpaid = false
}) => {
  const history = useHistory();
  const [currentLoyaltyPayment, setCurrentLoyaltyPayment] = useState({});
  const [iclpError, setIclpError] = useState(null);
  const [payoutError, setPayoutError] = useState(null);
  const [rollBackError, setRollBackError] = useState(false);
  const [hasLoyaltyAccountMismatchError, setLoyaltyAccountMismatchError] = useState(false);

  if (payoutError || iclpError) {
    throw new AgentPortalError(payoutError || iclpError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `${history.location.pathname}${history.location.search}`,
      additionalMessage: rollBackError ? <FormattedMessage id="refunds.loyalty.error.rollback" /> : ''
    });
  }

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const hasNoLoyaltyAccount = !membershipNumber;
  const bookingAttachedLoyaltyAccount = getBookingAttachedLoyaltyAccount({ additionalDetails });
  const loyaltyPayments = payments.filter((p) => p.method === codes.LOYALTY_POINTS && p.isSuccessful && !p.isPayout && !p.isBalance);

  const listOfLoyaltyPayments = useMemo(
    () =>
      loyaltyPayments.map(
        ({ id, refId, amountInOriginalCurrency, currency: loyaltyCurrency, reference: loyaltyPaymentReference, externalReference }) => {
          let loyaltyPoints;
          const loyaltyReference = loyaltyPaymentReference?.split?.('-') || [];
          if (loyaltyReference.length > 1 && Number.isInteger(Number(loyaltyReference[0]))) {
            const [loyaltyPointsInRef] = loyaltyReference;
            loyaltyPoints = Number(loyaltyPointsInRef);
          } else {
            loyaltyPoints = Math.round(amountInOriginalCurrency * MONEY_TO_POINTS_CONVERSION_RATE[loyaltyCurrency]);
          }

          const loyaltyPayoutAmount = additionalDetails
            .filter((ad) => ad.itemRef === id && ad.key.includes(ADDITIONAL_DETAILS_REFUND_KEY_PREFIX))
            .reduce((payout, { value }) => payout + Number(value), 0);

          const pointConversion = loyaltyPoints / amountInOriginalCurrency;
          const availableAmount = amountInOriginalCurrency - loyaltyPayoutAmount;
          const availablePoints = pointConversion ? Math.round(availableAmount * pointConversion) : Number(loyaltyPoints);
          const refundedAmount = availableAmount > totalPriceToBePaid ? totalPriceToBePaid : availableAmount;
          const refundedPoints = Math.round(refundedAmount * pointConversion);
          const isDisabled = !availableAmount || currency !== loyaltyCurrency;

          const handlePaymentClick = () =>
            setCurrentLoyaltyPayment({
              refundedAmount,
              refundedPoints,
              refId,
              loyaltyCurrency
            });

          return {
            id,
            refId,
            loyaltyPaymentReference,
            externalReference,
            isDisabled,
            handlePaymentClick,
            loyaltyCurrency,
            refundedAmount,
            refundedPoints,
            amount: amountInOriginalCurrency,
            loyaltyPoints,
            availableAmount,
            availablePoints
          };
        }
      ),
    [additionalDetails, currency, loyaltyPayments, totalPriceToBePaid]
  );

  const hasAvailableAmount = !!listOfLoyaltyPayments.reduce((acc, { availableAmount }) => acc + availableAmount, 0);

  const [adjustLoyaltyPoints, { loading: adjustLoyaltyPointsLoading }] = useMutation(ADJUST_POINTS_ENHANCEMENT);

  const [addNote, { loading: addNoteLoading }] = useMutation(ADD_NOTE_TO_BOOKING, {
    variables: {
      note: `Refund of tickets with discount with points for Club Eurostar account: ${membershipNumber},
 reward points : ${Math.abs(currentLoyaltyPayment.refundedPoints)}, accelerator points : 0, status points : 0.`,
      bookingReference
    }
  });

  const [addAdditionalDetails] = useMutation(ADD_ADDITIONAL_DETAILS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const isLoading = loading || adjustLoyaltyPointsLoading || addNoteLoading;

  const isLoyaltyMismatch =
    hasAvailableAmount && membershipNumber && bookingAttachedLoyaltyAccount && membershipNumber !== bookingAttachedLoyaltyAccount;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoyaltyMismatch) {
      setLoyaltyAccountMismatchError(true);

      return;
    }

    const completeTransaction = currentLoyaltyPayment.refundedAmount >= totalPriceToBePaid - MARGIN_REFUND;

    try {
      const comments = `Booking Reference: '${bookingReference}'. Refund in Voyager by ${username}`;

      await adjustLoyaltyPoints({
        variables: {
          membershipNumber,
          amountRewardPoints: Math.abs(currentLoyaltyPayment.refundedPoints),
          amountAcceleratorPoints: 0,
          amountStatusPoints: 0,
          reasonCode: 'Refunded Redemption',
          reasonText: comments,
          comments,
          actionBy: username,
          pnr: bookingReference
        }
      });

      await addNote();
    } catch (error) {
      setIclpError(error);

      return;
    }

    try {
      await handleRefundSubmit({
        membershipNumber,
        completeTransaction,
        paymentAmount: currentLoyaltyPayment.refundedAmount,
        totalPriceToBePaid,
        addPayoutAndBalance,
        updatePayments,
        addPayment,
        customer,
        bookingReference,
        reference,
        currency,
        method,
        loyaltyPoints: currentLoyaltyPayment.refundedPoints,
        isOverpaid
      });

      try {
        await addAdditionalDetails({
          variables: {
            bookingReference,
            additionalDetails: {
              paymentRefId: Number(currentLoyaltyPayment.refId),
              key: `REFUND-${generate()}`,
              value: String(currentLoyaltyPayment.refundedAmount)
            }
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to add additional details, booking: ${bookingReference}`, error);
      }
    } catch (error) {
      setPayoutError(error);
      try {
        const comments = `Booking Reference: '${bookingReference}'. Refund in Voyager by ${username}.`;

        await adjustLoyaltyPoints({
          variables: {
            membershipNumber,
            amountRewardPoints: -Math.abs(currentLoyaltyPayment.refundedPoints),
            amountAcceleratorPoints: 0,
            amountStatusPoints: 0,
            reasonCode: 'Refunded Redemption',
            reasonText: comments,
            comments,
            actionBy: username,
            pnr: bookingReference
          }
        });
      } catch (_) {
        setRollBackError(true);
      }
    }
  };

  const handleRedirectToClubEurostar = () =>
    history.push(`/clubeurostar/manage`, {
      prevPath: history.location.pathname,
      prevSearch: history.location.search,
      prevHash: history.location.hash
    });

  const handleCloseMismatchModal = () => setLoyaltyAccountMismatchError(!hasLoyaltyAccountMismatchError);

  return (
    <>
      <LoyaltyMismatchModal
        isOpen={hasLoyaltyAccountMismatchError}
        onClose={handleCloseMismatchModal}
        onCtaClick={handleRedirectToClubEurostar}
      />
      <SplitPayment
        title={<FormattedMessage id="payments.AmountToRefundByLoyalty_Points" />}
        subTitle={<FormattedMessage id="refunds.canBeSplit" />}
        label={<FormattedMessage id="refunds.recordRefund" />}
        loading={isLoading}
        disabled={hasNoLoyaltyAccount || !currentLoyaltyPayment.refundedAmount > 0}
        onSubmit={handleSubmit}
      >
        {hasAvailableAmount && !hasNoLoyaltyAccount
          ? listOfLoyaltyPayments.map(
              ({
                refId,
                loyaltyPaymentReference,
                externalReference,
                isDisabled,
                handlePaymentClick,
                loyaltyCurrency,
                amount,
                loyaltyPoints,
                availableAmount,
                availablePoints,
                refundedAmount,
                refundedPoints
              }) =>
                availableAmount ? (
                  <CardSelect
                    id={loyaltyPaymentReference || refId}
                    key={loyaltyPaymentReference || refId}
                    name="loyalty-reference"
                    testId={loyaltyPaymentReference || refId}
                    value={externalReference || refId}
                    disabled={isDisabled}
                    onClick={handlePaymentClick}
                  >
                    <div>
                      {currentLoyaltyPayment.refId === refId ? (
                        <RefundLabel data-cy={`refunded-amount-${externalReference || refId}`}>
                          <FormattedMessage
                            id="refunds.loyalty.refunded"
                            dataCy="loyalty-refunded"
                            values={{
                              amount: <FormattedCurrency currency={loyaltyCurrency} amount={refundedAmount} />,
                              points: refundedPoints
                            }}
                          />
                        </RefundLabel>
                      ) : null}
                      <FormattedMessage
                        id="refunds.loyalty.selectLoyalty"
                        dataCy="loyalty-select"
                        values={{
                          originalAmount: (
                            <FormattedCurrency
                              dataCy={`original-amount-${reference || refId}`}
                              currency={loyaltyCurrency}
                              amount={amount}
                            />
                          ),
                          originalPoints: loyaltyPoints,
                          availableAmount: (
                            <FormattedCurrency
                              dataCy={`available-amount-${reference || refId}`}
                              currency={loyaltyCurrency}
                              amount={availableAmount}
                            />
                          ),
                          availablePoints
                        }}
                      />
                    </div>
                  </CardSelect>
                ) : null
            )
          : null}
        <Errors data-cy="loyalty-payment-errors">
          {hasNoLoyaltyAccount && (
            <div>
              <FormattedMessage
                id="errorBoundary.notLoggedInLoyalty"
                data-test="no-loyalty-account-error"
                values={{
                  here: (
                    <HereLink onClick={handleRedirectToClubEurostar}>
                      <FormattedMessage id="errorBoundary.linkHere" />
                    </HereLink>
                  )
                }}
              />
            </div>
          )}
          {!hasAvailableAmount && (
            <div>
              <FormattedMessage id="payments.maximumRefund" data-test="maximum-refunds-error" />
            </div>
          )}
        </Errors>
      </SplitPayment>
    </>
  );
};

LoyaltyPointsRefund.propTypes = {
  currency: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  updatePayments: PropTypes.func,
  customer: customerShape,
  bookingReference: PropTypes.string,
  reference: PropTypes.string,
  method: PropTypes.string,
  loading: PropTypes.bool,
  payments: paymentsShape,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  username: PropTypes.string.isRequired,
  isOverpaid: PropTypes.bool
};

export default LoyaltyPointsRefund;
